import i18n from 'i18next';
import { enUS as en, es, fr, ja } from 'date-fns/locale';
import root from 'window-or-global';
import timeUtils from '../time-utils';
import unitsUtils from '../unit-converter';
import generalUtils from '../general-utils';
import dateUtilsConverters from '../date-utils-converters';
import type { Address, FullAddress } from '~/api/types';
import type { Options } from '../unit-converter';

// see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/language
const japaneseLanguageIsoCodes = ['ja', 'jpn'];
const spanishLanguageIsoCodes = ['es', 'spa'];

type GetTotalDistanceTextOptions = Omit<Options, 'language'>;

export const getTotalDistanceText = (
    totalMeters: number,
    options: GetTotalDistanceTextOptions = { useAbbreviated: true }
) => {
    const { numDecimalPlaces, useAbbreviated } = options;
    const distance = unitsUtils.getLocalDistance(totalMeters, {
        numDecimalPlaces
    });
    const units = unitsUtils.getLocalDistanceUnits({ useAbbreviated });
    return i18n.t(units, { distance });
};

export const formatDuration = (duration: number) => {
    const timeRemaining =
        dateUtilsConverters.convertMillisecondsToHoursAndMinutesAndSeconds(
            duration
        );

    return i18n.t('common:time.duration_hm', timeRemaining);
};

export const formatUnitsAndWeight = (unitsAndWeight: {
    numUnits: string;
    totalWeight: string;
}) => {
    const { numUnits, totalWeight } = unitsAndWeight;
    return i18n.t('unitsAndWeight', { numUnits, totalWeight });
};

export const formatNumInventory = (count: number) => {
    return i18n.t('numInventoryItems', { count });
};

export const formatNumStops = (numStops: number) => {
    return i18n.t('stopWithCount', { count: numStops });
};

const cleanFormattedAddress = (formattedAddress: string) => {
    const delimeter = '[,]';
    const emptyStartDelimeter = new RegExp(`^${delimeter}`, 'g');
    const emptyEndDelimeter = new RegExp(`${delimeter}$`, 'g');
    const emptyMiddleDelimiters = new RegExp(`\\s+${delimeter}`, 'g');

    return formattedAddress
        .trim()
        .replace(emptyMiddleDelimiters, '')
        .replace(emptyStartDelimeter, '')
        .replace(emptyEndDelimeter, '')
        .trim();
};

export const formatFullAddress = (address?: Partial<FullAddress> | string) => {
    if (!address) return '';

    if (typeof address === 'string') return address;

    const { name, addressLine1, addressLine2, city, state, zipcode, country } =
        address;

    const addressData = {
        addressLine1: addressLine1 || '',
        addressLine2: addressLine2 || '',
        city: city || '',
        state: state || '',
        zipcode: zipcode || ''
    };
    const formattedAddress = i18n.t('common:address.default', addressData);

    const fullAddressData = {
        name: name || '',
        address: cleanFormattedAddress(formattedAddress),
        country: country || ''
    };
    const formattedFullAddress = i18n.t('common:address.full', fullAddressData);

    return cleanFormattedAddress(formattedFullAddress);
};

export const formatAddress = (address?: Partial<Address> | string) => {
    let addressData = address;
    const isAddressObject = address && !(typeof address === 'string');
    if (isAddressObject) {
        const { addressLine1, addressLine2, city, state, zipcode } = address;
        addressData = {
            addressLine1,
            addressLine2,
            city,
            state,
            zipcode
        };
    }
    return formatFullAddress(addressData);
};

/**
 * Returns true/false if language set is Japanese
 */
export const isLanguageJapanese = () => {
    const { language } = root.navigator;
    const [languageCode] = (language || '').split('-');

    return japaneseLanguageIsoCodes.includes(languageCode);
};

/**
 * Returns true/false if language set is Spanish
 */
export const isLanguageSpanish = () => {
    const { language } = root.navigator;
    const [languageCode] = (language || '').split('-');

    return spanishLanguageIsoCodes.includes(languageCode);
};

/**
 * Returns formatted address inputs depending on detected browser locale
 */
export const formatAddressFieldsByLocale = <T>(
    fields: Partial<Record<keyof FullAddress, T>>
): T[] => {
    const { addressLine1, addressLine2, city, state, zipcode, country } =
        fields;

    const fieldOrder = isLanguageJapanese()
        ? [country, zipcode, state, city, addressLine1, addressLine2]
        : [addressLine1, addressLine2, city, state, zipcode, country];

    return fieldOrder.reduce((acc: T[], field) => {
        if (field) acc.push(field);
        return acc;
    }, []);
};

/**
 * Returns formatted address inputs depending on if language set is Japanese
 *
 * @deprecated use `formatAddressFieldsByLocale()` instead
 */
export const formatAddressInputsByLocale = <T>(
    englishFormatAddressInputs: T[]
): T[] => {
    const [addressLine1, addressLine2, ...japaneseFormatAddressInputs] =
        englishFormatAddressInputs;

    japaneseFormatAddressInputs.reverse();
    japaneseFormatAddressInputs.push(addressLine1, addressLine2);

    return isLanguageJapanese()
        ? japaneseFormatAddressInputs
        : englishFormatAddressInputs;
};

/**
 * Returns true/false value for US regions
 */
export const isUSRegion = () => {
    return (navigator.language || '')
        .split('-')
        .some((part) => part.toUpperCase() === 'US');
};

/**
 * Converts the given number of seconds to a localized duration
 *
 * @param {number} seconds - The number of seconds to convert
 * @param {string} [timeFormat='duration_hm'] - The format of the localized time. Default is 'duration_hm'
 * @returns {string} The localized duration string
 */
export const getLocalizedDuration = (
    seconds: number,
    timeFormat = 'duration_hm'
) => {
    const duration =
        dateUtilsConverters.convertSecondsToHoursAndMinutesAndSeconds(seconds);
    return i18n.t(`common:time.${timeFormat}`, duration);
};

export const getLocalizedDurationInMinutes = (
    seconds = 0,
    timeFormat = 'extended_min'
) => {
    const { minutes } = timeUtils.convertSecondsToMinutes(seconds);
    const roundedMinutes = generalUtils.roundToMaxDigitsAfterDecimal(minutes);
    return i18n.t(`common:time.${timeFormat}`, { minutes: roundedMinutes });
};

/**
 * Retrive the supported base language code based on the navigator language
 *
 * Supported langauges are defined by `~/i18n.js`
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Glossary/BCP_47_language_tag}
 */
export const getSupportedLanguage = () => {
    if (!navigator.language) {
        return;
    }

    const [baseLanguage] = navigator.language.split('-');
    const supportedLanguages = i18n.options.supportedLngs as string[];
    const isSupportedLanguage = supportedLanguages.includes(baseLanguage);
    return isSupportedLanguage ? baseLanguage : undefined;
};

/**
 * Retrieve the region code from the browser locale
 *
 * Extracts the region subtag from `navigator.language` (e.g. `en-US` -> `US`)
 * and validates it as an ISO 3166-1 alpha-2 code. Returns `undefined` when no
 * valid region can be derived so callers can omit the value entirely.
 *
 * This is used to localize the Google Maps SDK via its `region` parameter,
 * which expects a two-letter ccTLD/region code.
 *
 * @see {@link https://developers.google.com/maps/documentation/javascript/localization#Region}
 * @see {@link https://developer.mozilla.org/en-US/docs/Glossary/BCP_47_language_tag}
 */
export const getRegion = (): string | undefined => {
    if (!navigator.language) {
        return;
    }

    let region: string | undefined;
    try {
        region = new Intl.Locale(navigator.language).region ?? undefined;
    } catch {
        // `navigator.language` was not a well-formed BCP 47 tag
        region = undefined;
    }

    if (!region) {
        return;
    }

    const normalizedRegion = region.toUpperCase();
    const isValidIsoRegion = /^[A-Z]{2}$/.test(normalizedRegion);

    return isValidIsoRegion ? normalizedRegion : undefined;
};

/**
 * Retrieve the supported display locale
 *
 * Supported locales are based on the supported languages defined in `i18n`
 */
export const getDisplayLocale = () => {
    const localeMap: Record<string, Locale> = {
        en,
        es,
        fr,
        ja
    };

    const baseLang = getSupportedLanguage() || '';
    return localeMap[baseLang] || en;
};

export default {
    getTotalDistanceText,
    formatDuration,
    formatUnitsAndWeight,
    formatNumInventory,
    formatNumStops,
    formatAddress,
    isLanguageJapanese,
    isLanguageSpanish,
    formatAddressInputsByLocale,
    getLocalizedDuration,
    isUSRegion,
    getLocalizedDurationInMinutes,
    getDisplayLocale,
    getSupportedLanguage,
    getRegion
};
